interface SegmentDefinition {
    Name: string;
    Type: string;
}

export interface TagSegment extends SegmentDefinition {
    TagIds: string[];
    HasTags: boolean;
    Bool: TagSegmentBool;
}

export interface RecipientSegment extends SegmentDefinition {
    IssueId: string;
    ReceivedMailing: boolean;
    Date: Date | null;
    TimeSpanSince: string | null;
    Operator: RecipientSegmentOperator | null;
}

export enum RecipientSegmentOperator {
    Before = 1,
    After = 2,
}

export interface SmsRecipientSegment extends SegmentDefinition {
    SmsMessageId: string;
    Received: boolean;
    Date: Date | null;
    TimeSpanSince: string | null;
    Operator: SmsRecipientSegmentOperator | null;
}

export enum SmsRecipientSegmentOperator {
    Before = 1,
    After = 2,
}

export enum TagSegmentBool {
    All = 1,
    Any = 2,
}

export interface PropertySegment extends SegmentDefinition {
    Property: PropertySegmentProperty;
    Operator: PropertySegmentOperator;
    Value: any;
}

export interface Condition extends SegmentDefinition {
    Key: string;
    Operator: PropertySegmentOperator;
    Value: string;
}

export enum PropertySegmentProperty {
    Name,
    FirstName,
    LastName,
    Email,
    Gender,
    Birthday,
    CompanyName,
    JobTitle,
    Industry,
    Website,
    Salutation,
    StreetAddress,
    City,
    PostalCode,
    State,
    Country,
    Region,
    Created,
    IsActive,
    HasBounced,
    Custom1,
    Custom2,
    Custom3,
    Custom4,
    Custom5,
    Custom6,
    Custom7,
    Custom8,
    Custom9,
    Custom10,
    CustomLong1,
    CustomLong2,
    CustomNumeric1,
    CustomNumeric2,
    CustomNumeric3,
    CustomNumeric4,
    CustomNumeric5,
    CustomNumeric6,
}

export enum PropertySegmentOperator {
    Equals = 0,
    NotEquals = 1,
    LessThan = 2,
    GreaterThan = 3,
    Contains = 4,
    NotContains = 5,
    GreaterThanOrEqual = 6,
    LessThanOrEqual = 7,
    Empty = 9,
    NotEmpty = 10,
}

export enum MemberSegmentOperator {
    Before = 1,
    After = 2,
}

export enum IssueRecipientLimiterSegmentTimeSpan {
    ToDay = 1,
    ThisWeek = 2,
    ThisMonth = 3,
}

export enum InactiveContactsPeriod {
    Month = 1,
    Year = 2,
}

export enum EventParticipantStatus {
    Registered = 0,
    Invited = 1,
    Waitlist = 2,
    Accepted = 3,
    Declined = 4,
    Cancelled = 5,
    Attended = 6,
    NoShow = 7,
}

export enum ContactRelativeSegmentOperator {
    Before = 1,
    After = 2,
}

export interface ContactRelativeSegmentProperty extends SegmentDefinition {
    Property: ContactRelativeSegmentPropertyProperty;
    Operator: ContactRelativeSegmentOperator;
    Value: any;
}

export enum ContactRelativeSegmentPropertyProperty {
    Created, // DateTime
    Modified, // DateTime
    Birthday, // DateTime
    CustomDate1, // DateTime
    CustomDate2, // DateTime
    LastNpsDate,
}

export enum DataPointDataType {
    Text,
    Date,
    RelativeDate,
    Number,
    Boolean,
}

const enumKeys = (en: any) => {
    let values = Object.values(en);
    return values.splice(0, values.length / 2);
};

export const ConditionOptions: any = {
    TagSegment: {
        HasTags: [
            { resourceKey: 'Has', value: true },
            { resourceKey: 'HasNot', value: false },
        ],
        Bool: [
            { resourceKey: 'All', value: TagSegmentBool.All },
            { resourceKey: 'Any', value: TagSegmentBool.Any },
        ],
    },
    PropertySegment: {
        Property: enumKeys(PropertySegmentProperty).map((key: any) => ({ resourceKey: key, value: PropertySegmentProperty[key] })),
        Operator: enumKeys(PropertySegmentOperator).map((key: any) => ({ resourceKey: key, value: PropertySegmentOperator[key] })),
    },
    ContactRelativeSegmentProperty: {
        Property: enumKeys(ContactRelativeSegmentPropertyProperty).map((key: any) => ({
            resourceKey: key,
            value: ContactRelativeSegmentPropertyProperty[key],
        })),
        Operator: enumKeys(ContactRelativeSegmentOperator).map((key: any) => ({ resourceKey: key, value: ContactRelativeSegmentOperator[key] })),
    },
    RecipientSegment: {
        ReceivedMailing: [
            { resourceKey: 'Has', value: true },
            { resourceKey: 'HasNot', value: false },
        ],
        Operator: [
            { resourceKey: '', value: null },
            ...enumKeys(RecipientSegmentOperator).map((key: any) => ({ resourceKey: key, value: RecipientSegmentOperator[key] })),
        ],
    },
    SmsRecipientSegment: {
        Received: [
            { resourceKey: 'Has', value: true },
            { resourceKey: 'HasNot', value: false },
        ],
        Operator: [
            { resourceKey: '', value: null },
            ...enumKeys(SmsRecipientSegmentOperator).map((key: any) => ({ resourceKey: key, value: SmsRecipientSegmentOperator[key] })),
        ],
    },
    MemberSegment: {
        InList: [
            { resourceKey: 'Is', value: true },
            { resourceKey: 'IsNot', value: false },
        ],
        Operator: [
            { resourceKey: '', value: null },
            ...enumKeys(MemberSegmentOperator).map((key: any) => ({ resourceKey: key, value: MemberSegmentOperator[key] })),
        ],
    },
    IssueRecipientLimiterSegment: {
        HasReceivedEmail: [
            { resourceKey: 'Is', value: true },
            { resourceKey: 'IsNot', value: false },
        ],
        TimeSpanSince: [
            { resourceKey: '', value: null },
            ...enumKeys(IssueRecipientLimiterSegmentTimeSpan).map((key: any) => ({ resourceKey: key, value: IssueRecipientLimiterSegmentTimeSpan[key] })),
        ],
    },
    InactiveContactsWithinTimePeriodSegment: {
        TimeSpanSince: [
            { resourceKey: '', value: null },
            ...enumKeys(InactiveContactsPeriod).map((key: any) => ({ resourceKey: key, value: InactiveContactsPeriod[key] })),
        ],
    },
    SegmentMemberSegment: {
        InSegment: [
            { resourceKey: 'Is', value: true },
            { resourceKey: 'IsNot', value: false },
        ],
    },
    InteractionSegment: {
        HasInteractions: [
            { resourceKey: 'Has', value: true },
            { resourceKey: 'HasNot', value: false },
        ],
    },
    SurveyRespondentSegment: {
        HasResponded: [
            { resourceKey: 'Has', value: true },
            { resourceKey: 'HasNot', value: false },
        ],
        HasReceivedEmail: [
            { resourceKey: 'Has', value: true },
            { resourceKey: 'HasNot', value: false },
        ],
    },
    SurveyAnswerSegment: {
        HasResponded: [
            { resourceKey: 'Has', value: true },
            { resourceKey: 'HasNot', value: false },
        ],
    },
    EventParticipantIssueSegment: {
        HasStatus: [
            { resourceKey: 'Has', value: true },
            { resourceKey: 'HasNot', value: false },
        ],
        HasReceivedEmail: [
            { resourceKey: 'Has', value: true },
            { resourceKey: 'HasNot', value: false },
        ],
        Statuses: enumKeys(EventParticipantStatus).map((key: any) => ({ resourceKey: key, value: EventParticipantStatus[key] })),
    },
    EventParticipantSmsSegment: {
        HasStatus: [
            { resourceKey: 'Has', value: true },
            { resourceKey: 'HasNot', value: false },
        ],
        HasReceivedSms: [
            { resourceKey: 'Has', value: true },
            { resourceKey: 'HasNot', value: false },
        ],
        Statuses: enumKeys(EventParticipantStatus).map((key: any) => ({ resourceKey: key, value: EventParticipantStatus[key] })),
    },
    EventParticipantSegment: {
        HasStatus: [
            { resourceKey: 'Has', value: true },
            { resourceKey: 'HasNot', value: false },
        ],
        HasReceivedEmail: [
            { resourceKey: 'Has', value: true },
            { resourceKey: 'HasNot', value: false },
        ],
        Statuses: enumKeys(EventParticipantStatus).map((key: any) => ({ resourceKey: key, value: EventParticipantStatus[key] })),
    },
    EventParticipantStatusSegment: {
        HasStatus: [
            { resourceKey: 'Has', value: true },
            { resourceKey: 'HasNot', value: false },
        ],
        Statuses: enumKeys(EventParticipantStatus).map((key: any) => ({ resourceKey: key, value: EventParticipantStatus[key] })),
    },
    DataPointSegment: {
        Name: null,
        Type: 'DataPointSegment',
        Conditions: [
            {
                key: null,
                Value: null,
                Type: null,
                Operator: enumKeys(PropertySegmentOperator).map((key: any) => ({ resourceKey: key, value: PropertySegmentOperator[key] })),
            },
        ],
    },
};
export const ConditionList: any = {
    AgeSegment: {
        Name: null,
        Type: 'AgeSegment',
        MinAge: null,
        MaxAge: null,
    },
    TagSegment: {
        Name: null,
        Type: 'TagSegment',
        TagIds: [],
        HasTags: true,
        Bool: 1,
    },
    PropertySegment: {
        Name: null,
        Type: 'PropertySegment',
        Property: 0,
        Operator: 0,
        Value: '',
    },
    RecipientSegment: {
        Name: null,
        Type: 'RecipientSegment',
        IssueId: null,
        ReceivedMailing: true,
        Date: null,
        TimeSpanSince: null,
        Operator: null,
    },
    SmsRecipientSegment: {
        Name: null,
        Type: 'SmsRecipientSegment',
        SmsMessageId: null,
        Received: true,
        Date: null,
        TimeSpanSince: null,
        Operator: null,
    },
    MemberSegment: {
        Name: null,
        Type: 'MemberSegment',
        ListId: null,
        InList: true,
        Date: null,
        TimeSpanSince: null,
        Operator: null,
    },
    IssueRecipientLimiterSegment: {
        Name: null,
        Type: 'IssueRecipientLimiterSegment',
        Value: null,
        TimeSpan: null,
        HasReceivedEmail: true,
    },
    InactiveContactsWithinTimePeriodSegment: {
        Name: null,
        Type: 'InactiveContactsWithinTimePeriodSegment',
        Value: null,
        TimeSpan: null,
    },
    SegmentMemberSegment: {
        Name: null,
        Type: 'SegmentMemberSegment',
        SegmentId: null,
        InSegment: true,
    },
    InteractionSegment: {
        Name: null,
        Type: 'InteractionSegment',
        ObjectId: null,
        HasInteractions: true,
    },
    SurveyRespondentSegment: {
        Name: null,
        Type: 'SurveyRespondentSegment',
        SurveyId: null,
        HasResponded: true,
        HasReceivedEmail: true,
    },
    SurveyAnswerSegment: {
        Name: null,
        Type: 'SurveyAnswerSegment',
        SurveyId: null,
        QuestionId: null,
        QuestionOptionId: null,
        HasResponded: true,
    },
    EventParticipantIssueSegment: {
        Name: null,
        Type: 'EventParticipantIssueSegment',
        EventId: null,
        HasStatus: true,
        HasReceivedEmail: true,
        Statuses: [],
    },
    EventParticipantSmsSegment: {
        Name: null,
        Type: 'EventParticipantSmsSegment',
        EventId: null,
        HasStatus: true,
        HasReceivedSms: true,
        Statuses: [],
    },
    EventParticipantSegment: {
        Name: null,
        Type: 'EventParticipantSegment',
        EventId: null,
        HasStatus: true,
        HasReceivedEmail: true,
        Statuses: [],
    },
    EventParticipantStatusSegment: {
        Name: null,
        Type: 'EventParticipantStatusSegment',
        EventId: null,
        HasStatus: true,
        HasReceivedEmail: true,
        Statuses: [],
    },
    ContactRelativeSegmentProperty: {
        Name: null,
        Type: 'ContactRelativeSegmentProperty',
        Property: 0,
        Operator: 0,
        Value: '',
    },
    DataPointSegment: {
        Name: null,
        Type: 'DataPointSegment',
        Conditions: [
            {
                key: '',
                Value: '',
                Type: 0,
                Operator: 0,
            },
        ],
    },
};

export const MemberSegment = {
    Name: null,
    Type: 'MemberSegment',
    ListId: null,
    InList: [
        { resourceKey: 'Is', value: true },
        { resourceKey: 'IsNot', value: false },
    ],
    Date: null,
    TimeSpanSince: null,
    Operator: [
        { resourceKey: '', value: null },
        ...enumKeys(MemberSegmentOperator).map((key: any) => ({ resourceKey: key, value: MemberSegmentOperator[key] })),
    ],
};

export const groupFactory = ({ Name = null, Bool = 1, Segments = [] }) => ({
    Name,
    Type: 'SegmentGroup',
    Bool,
    Segments,
});
